<template>
    <template v-if="!features['new-resource-card-in-events']">
        <template v-if="!shouldUseNewCard">
            <div v-if="event.data" class="event-card" :data-testid="baseDataTestid">
                <EventThumbnail :event="event" class="thumbnail" :data-testid="`${baseDataTestid}--thumbnail`" />

                <div class="top-info">
                    <CategoryPillList :document="event" />
                </div>

                <PrismicLink v-if="isFilled.link(titleLink)" :field="titleLink" class="event-text">
                    <h3 class="title" :data-testid="`${baseDataTestid}--title`">{{ event.data.title }}</h3>
                </PrismicLink>
                <div v-else class="event-text">
                    <h3 class="title" :data-testid="`${baseDataTestid}--title`">{{ event.data.title }}</h3>
                </div>
                <PrismicLink v-if="isUpcomingEvent && isFilled.link(event.data?.signupLink)" :field="event.data.signupLink" class="link">
                    {{ t('resources.events.signup') }}
                </PrismicLink>
                <PrismicLink v-else-if="isFilled.link(event.data?.replayLink)" :field="event.data?.replayLink ?? {link_type: 'Any'}" class="link">
                    {{ t('resources.events.watchReplay') }}
                </PrismicLink>
            </div>
        </template>
        <template v-else>
            <div v-if="event.data" class="event-card event-card--withCursor" :data-testid="baseDataTestid">
                <NuxtLink v-slot="{href, navigate}" :to="`/resources/event/${event.uid}`" custom>
                    <section class="event-text" @click="navigate">
                        <EventThumbnail :event="event" class="thumbnail" :data-testid="`${baseDataTestid}--thumbnail`" />
                        <div class="top-info">
                            <CategoryPillList :document="event" @click.stop />
                        </div>
                        <div class="event-text">
                            <a :href="href">
                                <h3 class="title" :data-testid="`${baseDataTestid}--title`">{{ event.data.title }}</h3>
                            </a>
                        </div>
                        <div>
                            <span class="link">{{ linkText }}</span>
                        </div>
                    </section>
                </NuxtLink>
            </div>
        </template>
    </template>
    <template v-else>
        <ResourceCard
            v-if="event.data"
            class="event-card"
            document-type="event"
            :to="redirectLink"
            :title="event.data.title"
            :excerpt="event.data.excerpt"
            :image="event.data.thumbnail"
            :is-upcoming-event="isUpcomingEvent"
            :data-testid="baseDataTestid"
            :resource-categories="(props.event.data?.resourceCategories as {resourceCategory: ResourceCategoryDocument}[]) ?? undefined"
            :category="event.data.category"
        >
            <template #image-overlay>
                <EventImageOverlay :date="event.data.date" :is-upcoming-event="isUpcomingEvent" />
            </template>
        </ResourceCard>
    </template>
</template>

<script lang="ts" setup>
    import {computed, type ComputedRef} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import type {FilledContentRelationshipField} from '@prismicio/types';
    import {PrismicLink} from '@prismicio/vue';
    import type {EventDocument, EventDocumentData, ResourceCategoryDocument} from '@prismicio-types';
    import {useTranslation} from '#imports';
    import linkResolver from '~/app/prismic/link-resolver';
    import CategoryPillList from '~/components/CategoryPillList.vue';
    import EventImageOverlay from '~/components/EventImageOverlay.vue';
    import EventThumbnail from '~/components/EventThumbnail.vue';
    import ResourceCard from '~/components/ResourceCard.vue';
    import {createDateHelpers} from '~/composables/dateHelpers';
    import {useFeatureFlagsStore} from '~/stores/feature-flags.store';

    const {features} = useFeatureFlagsStore();
    const {t} = useTranslation();

    const props = defineProps<{
        event: EventDocument | FilledContentRelationshipField<'event', string, EventDocumentData>;
    }>();

    // Computed values
    const baseDataTestid = computed(() => props.event.uid ?? props.event.id);
    const shouldUseNewCard = computed(() => {
        if (!props.event?.data) return false;
        const sl = props.event.data.signupLink;
        if (!isFilled.link(sl)) return true;
        if (sl.link_type === 'Web') return !sl.url;
        return !('url' in sl && sl.url);
    });
    const {isBefore, isSameDay} = createDateHelpers();
    const isUpcomingEvent = computed(
        (): boolean =>
            isBefore(new Date(), new Date(props.event.data?.date as string)) || isSameDay(new Date(), new Date(props.event.data?.date as string)),
    );

    const linkText = computed(() => (isUpcomingEvent.value ? t('resources.events.signup') : t('resources.events.watchReplay')));
    const titleLink = computed(() => (isUpcomingEvent.value ? props.event.data?.signupLink : props.event.data?.replayLink));
    const oldCardLink = computed(() => {
        if (isUpcomingEvent.value && isFilled.link(props.event.data?.signupLink)) return props.event.data.signupLink.url;
        if (isFilled.link(props.event.data?.replayLink)) return props.event.data.replayLink.url;
        if (isFilled.link(titleLink.value)) return titleLink.value.url;
        return '/resources/events';
    }) as ComputedRef<string>;
    const redirectLink = computed(() => (shouldUseNewCard.value ? linkResolver(props.event) : oldCardLink.value));
</script>

<style lang="scss">
    @use '~/assets/styles/variables' as *;

    .event-card {
        width: auto;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        position: relative;

        &--withCursor {
            cursor: pointer;
        }

        .thumbnail {
            width: 100%;
            height: 240px;
            object-fit: cover;
            border-radius: 24px;
            overflow: hidden;

            &.success-thumbnail {
                object-fit: contain;
                height: auto;
            }

            @media (max-width: $mobile-breakpoint) {
                max-height: 70vw;
            }
        }

        .top-info {
            margin-top: var(--joy-core-spacing-4);
            display: flex;
            justify-content: space-between;
            align-items: center;
        }

        .event-text {
            flex-grow: 1;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            width: 100%;
        }

        .title {
            margin-top: var(--joy-core-spacing-5);
            margin-bottom: var(--joy-core-spacing-9);
        }

        .link {
            @include link;
            margin-top: var(--joy-core-spacing-5);
        }
    }
</style>
